'use client'

import { ChevronLeftIcon } from '@heroicons/react/24/outline'

import { Button } from '@src/components/Button/Button'
import { LocalizedLink } from '@src/components/LocalizedLink/LocalizedLink'
import { useTopNavV5 } from '@src/components/TopNavV5/TopNavV5Context'
import { TopNavV5SubMenuTileSlider } from '@src/components/TopNavV5/TopNavV5Mobile/TopNavV5SubMenuTileSlider'
import type { LinkItemWithStamp, TopNavV5SubMenuItem } from '@src/components/TopNavV5/TopNavV5Props'
import { useTrackSelectNavCta } from '@src/utils/analytics.util'
import { cn } from '@src/utils/tailwind'

export const TopNavV5MobileSubMenu = () => {
  const trackSelectNavCta = useTrackSelectNavCta()
  const { mobileState, mobileDispatch } = useTopNavV5()

  const miniMenu = mobileState.miniMenu
  const subMenu =
    mobileState.state === 'left-menu-sub-menu-open'
      ? mobileState.leftMenu?.items[mobileState.leftMenuIndex ?? 0]
      : mobileState.subMenu

  const hasMiniMenu = Boolean(miniMenu) && mobileState.state !== 'left-menu-sub-menu-open'

  const renderTitle = () => {
    const title = hasMiniMenu ? miniMenu?.title : subMenu?.title
    return (
      <div className="flex h-14 items-center border-b border-white/10 p-4">
        <button
          onClick={() => {
            if (mobileState.state === 'left-menu-sub-menu-open') {
              mobileDispatch({ type: 'BACK_FROM_LEFT_MENU_SUB_MENU' })
            } else {
              mobileDispatch({ type: 'BACK_FROM_SUB_MENU' })
            }
          }}
          className="flex items-center gap-2"
        >
          <ChevronLeftIcon className="size-5 text-bodi-green-bright-500" />
          <p className="text-heading-4 text-left font-lateral">{title}</p>
        </button>
      </div>
    )
  }

  const renderCtaMobile = () => {
    if (hasMiniMenu) return null

    const content = (
      <Button className="w-full" size="medium">
        {subMenu?.ctaMobile?.title}
      </Button>
    )
    return (
      subMenu?.hasCtaMobile &&
      subMenu?.ctaMobile?.title && (
        <>
          {/* spacer for safari */}
          <div className="h-[170px]"></div>
          <div className="fixed bottom-0 left-0 h-[85px] w-screen border-t border-bodi-gray-dark bg-bodi-black-text px-4 py-5">
            <div className="font-medium text-bodi-green-bright-500">
              {subMenu?.ctaMobile?.href ? (
                <LocalizedLink
                  data-testid="menu-featured-item-mobile"
                  onClick={() => {
                    trackSelectNavCta({ 'cta url': subMenu?.ctaMobile?.href })
                    mobileDispatch({ type: 'CLOSE_FROM_SUB_MENU' })
                  }}
                  href={subMenu?.ctaMobile?.href ?? ''}
                  rel={subMenu?.ctaMobile?.newTab ? 'noopener noreferrer' : ''}
                  target={subMenu?.ctaMobile?.newTab ? '_blank' : '_self'}
                >
                  {content}
                </LocalizedLink>
              ) : (
                <div>{content}</div>
              )}
            </div>
          </div>
        </>
      )
    )
  }

  const renderSubItem = (item: TopNavV5SubMenuItem, itemIdx: number) => {
    return (
      <div
        key={`${item.title}-${itemIdx}`}
        className={cn('border-t border-white/10 px-4 py-6', {
          'border-t-0': itemIdx === 0,
        })}
      >
        <div className="mb-2 text-body-md font-medium">
          {item.isLink ? (
            <LocalizedLink
              className="inline-block hover:underline"
              data-testid={`sub-menu-${itemIdx}`}
              onClick={() => {
                trackSelectNavCta({ 'cta url': item?.link?.href ?? '' })
                mobileDispatch({ type: 'CLOSE_FROM_SUB_MENU' })
              }}
              href={item?.link?.href ?? ''}
              key={`${item.title}-${itemIdx}`}
              rel={item?.link?.newTab ? 'noopener noreferrer' : ''}
              target={item?.link?.newTab ? '_blank' : '_self'}
            >
              {item.title}
            </LocalizedLink>
          ) : (
            <div>{item.title}</div>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {item.links
            ?.filter((subItem) => Boolean(subItem.title))
            .map((subItem, subItemIdx) => (
              <LocalizedLink
                className="flex w-fit items-center gap-1 text-body-sm font-medium text-bodi-gray-butters hover:underline"
                data-testid={`sub-menu-${subItemIdx}`}
                onClick={() => {
                  trackSelectNavCta({ 'cta url': subItem?.href })
                  mobileDispatch({ type: 'CLOSE_FROM_SUB_MENU' })
                }}
                href={subItem?.href ?? ''}
                key={`${subItem.title}-${subItemIdx}`}
                rel={subItem?.newTab ? 'noopener noreferrer' : ''}
                target={subItem?.newTab ? '_blank' : '_self'}
              >
                <div>{subItem.title}</div>
                {subItem.stamp && (
                  <>
                    {'-'}
                    <span className="text-body-sm font-medium text-bodi-green-bright-500">
                      {subItem.stamp}
                    </span>
                  </>
                )}
              </LocalizedLink>
            ))}
        </div>
      </div>
    )
  }

  const renderMiniMenuSubItem = (item: LinkItemWithStamp, itemIdx: number) => (
    <LocalizedLink
      className="text-body-sm font-medium text-bodi-gray-butters hover:underline"
      data-testid={`sub-menu-${itemIdx}`}
      onClick={() => {
        trackSelectNavCta({ 'cta url': item?.href })
        mobileDispatch({ type: 'CLOSE_FROM_SUB_MENU' })
      }}
      href={item?.href ?? ''}
      key={`${item.title}-${itemIdx}`}
      rel={item?.newTab ? 'noopener noreferrer' : ''}
      target={item?.newTab ? '_blank' : '_self'}
      dangerouslySetInnerHTML={{ __html: item.title ?? '' }}
    />
  )

  return (
    <div className="relative">
      {renderTitle()}
      <div className="relative flex flex-col">
        {!hasMiniMenu && subMenu?.items?.filter((item) => Boolean(item.title)).map(renderSubItem)}
        {hasMiniMenu && (
          <div className="flex flex-col gap-3 px-4 py-6">
            {miniMenu?.items?.filter((item) => Boolean(item.title)).map(renderMiniMenuSubItem)}
          </div>
        )}
        {!hasMiniMenu && <TopNavV5SubMenuTileSlider />}
      </div>
      {renderCtaMobile()}
    </div>
  )
}
